import type { HistorySearchItem } from "../types/suggestion";
import type { TrackingData } from "../types/tracking";

/*                                                         */
/*         */
import templateString from "../../public/templates/historyLayer.mustache";
import {
  arrowTemplate,
  checkboxIconTemplate,
  checkboxInputTemplate,
  footerLiTemplate,
  headlineTemplate,
  itemLabelTemplate,
  keywordsTemplate,
} from "./history.templates";

import { ELEMENT, FALLBACK_SEARCH_URL, HISTORY_MODE, TRACK_LABEL } from "../types/constants";
import { SquirrelURL } from "../helper";
import { SanSearchHistory } from "../types/tracking/constants";
import { HISTORY_LABEL } from "../types/label";
import { DATA, SELECTOR } from "../types/element/constants";

const Mustache = window.Mustache;

type HistoryRenderItem =
  | { term: string; checked: boolean; activeFilters: string | undefined; target: string; historyIndex: number }
  | undefined;

export class SearchHistory {
  private _mode: HISTORY_MODE;

  constructor() {
    this._mode = HISTORY_MODE.NORMAL;
  }

  get suggestLayer(): HTMLDivElement {
    return document.querySelector(SELECTOR.LAYER) as HTMLDivElement;
  }

  get searchField(): HTMLInputElement {
    return document.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
  }

  get mode(): HISTORY_MODE {
    return this._mode;
  }

  show() {
    const response: HistorySearchItem[] = window.o_squirrel.submitHistory.retrieveAllSearch();
    const renderedTemplate: string = this.renderHistory(response);
    this.updateSuggestLayer(renderedTemplate);
    window.o_squirrel.searchSuggest.showLayer();
  }

  private updateSuggestLayer(template: string) {
    const historyLayer = document.querySelector(SELECTOR.HISTORY_LAYER) as HTMLDivElement;
    window.o_squirrel.searchSuggest.updateSuggestionLayer.bind(historyLayer)(template);
  }

  clear() {
    this._mode = HISTORY_MODE.NORMAL;
    this.removeChecked();
  }

  removeChecked() {
    Array.from(this.suggestLayer.querySelectorAll<HTMLSpanElement>(SELECTOR.HISTORY_TERM)).forEach(
      (elem: HTMLSpanElement) => {
        elem.removeAttribute("checked");
      },
    );
  }

  clickEditAction() {
    this.toggleMode();
    if (this.mode == HISTORY_MODE.SELECT) {
      this.removeChecked();
    }
    this.show();
  }

  clickTermAction(evt: Event) {
    if (this.mode === HISTORY_MODE.SELECT) {
      this.selectItemAction(evt);
    } else {
      this.submit(evt);
    }
  }

  clickDeleteAllAction() {
    if (this.mode === HISTORY_MODE.SELECT) {
      this.deleteSelected();
    } else {
      this.deleteAll();
    }
  }

  private toggleMode() {
    this._mode = this.mode === HISTORY_MODE.SELECT ? HISTORY_MODE.NORMAL : HISTORY_MODE.SELECT;
  }

  private renderHeadline(selectMode: boolean): string {
    return Mustache.render(headlineTemplate, {
      selectMode,
      labelEdit: HISTORY_LABEL.EDIT,
      labelAbort: HISTORY_LABEL.ABORT,
    });
  }

  private renderKeyword(selectMode: boolean, searches: HistoryRenderItem[]): string {
    const checkboxItem: string = selectMode ? checkboxInputTemplate : checkboxIconTemplate;
    return Mustache.render(
      keywordsTemplate,
      { selectMode, searches },
      {
        checkboxItem,
        itemLabelTemplate,
        arrowTemplate,
      },
    );
  }

  private renderFooter(itemSelected: boolean): string {
    const label: string = itemSelected ? HISTORY_LABEL.DELETE_SELECTED : HISTORY_LABEL.DELETE_ALL;
    return Mustache.render(footerLiTemplate, { label });
  }

  renderHistory(response: HistorySearchItem[]): string {
    const searches = this.getSearches(response);
    const selectMode = this.mode === HISTORY_MODE.SELECT;
    const itemSelected = searches.some((search) => search?.checked);

    return Mustache.render(
      templateString,
      {},
      {
        headlineTemplate: this.renderHeadline(selectMode),
        keywordTemplate: this.renderKeyword(selectMode, searches),
        footerLiTemplate: this.renderFooter(itemSelected && this.mode == HISTORY_MODE.SELECT),
      },
    );
  }

  private deleteSelected() {
    type E = HTMLInputElement;
    const items: number[] = Array.from(this.suggestLayer.querySelectorAll<E>(SELECTOR.HISTORY_ITEM_SELECT))
      .filter(({ checked }: Partial<E>): boolean => !!checked)
      .map((e: E) => Number(e.closest(SELECTOR.HISTORY)!.getAttribute(DATA.HISTORY_INDEX)));

    if (items.length <= 0) {
      this.deleteAll();
    } else {
      window.o_squirrel.submitHistory.deleteSearches(items);
      this.show();
      if (window.o_squirrel.submitHistory.isEmpty()) {
        this.updateSuggestLayer("");
        this.searchField.focus();
      }
    }
  }

  private deleteAll() {
    window.o_squirrel.submitHistory.deleteAllSearches();
    this.updateSuggestLayer("");
    this.searchField.focus();
  }

  private selectItemAction(evt: Event) {
    const elem: HTMLInputElement = evt.target as HTMLInputElement;
    if (elem.type === "checkbox") {
      document.querySelector(SELECTOR.HISTORY_DELETEALL)!.querySelector(SELECTOR.HISTORY_TERM)!.textContent =
        document.querySelectorAll(`${SELECTOR.HISTORY_ITEM_SELECT}[type="checkbox"]:checked`).length === 0
          ? HISTORY_LABEL.DELETE_ALL
          : HISTORY_LABEL.DELETE_SELECTED;
    } else {
      this.submit(evt);
    }
  }

  getSearches(historyItems: HistorySearchItem[]): HistoryRenderItem[] {
    const uniqueSearches: { [key: string]: HistorySearchItem } = {};
    for (let i = 0; i < historyItems.length; i++) {
      const search = historyItems[i];
      const key = `${search.term}_${search.dresonMap?.getAllFilterNames.join(",")}`;
      if (!uniqueSearches[key] || uniqueSearches[key].date < search.date) {
        uniqueSearches[key] = search;
      }
    }
    const uniqueSearchesArray = Object.values(uniqueSearches).sort((a, b) => b.date - a.date);
    return historyItems
      .map((item: HistorySearchItem, index: number) => {
        const uniqueItem = uniqueSearchesArray.find(
          (search: HistorySearchItem) =>
            search.term === item.term &&
            search.dresonMap?.getAllFilterNames.join(",") === item.dresonMap?.getAllFilterNames.join(",") &&
            search.date === item.date,
        );
        const activeFiltersAvailable =
          uniqueItem?.dresonMap !== undefined && uniqueItem?.dresonMap.getAllFilterNames.length > 0;
        const target: string = activeFiltersAvailable ? uniqueItem?.dresonMap?.get("_redirect") + "" : "";
        const activeFilters = activeFiltersAvailable ? uniqueItem?.dresonMap?.getAllFilterNames.join(", ") : undefined;
        if (uniqueItem) {
          return {
            term: uniqueItem.term,
            checked: uniqueItem.checked ?? false,
            activeFilters,
            target,
            historyIndex: index,
          };
        }
        return undefined as HistoryRenderItem;
      })
      .filter((item: HistoryRenderItem): boolean => item !== undefined)
      .slice(0, 5);
  }

  private submit(evt: Event) {
    const elem: HTMLInputElement = evt.target as HTMLInputElement;
    const target: HTMLLIElement = elem.closest(ELEMENT.LI) as HTMLLIElement;
    const termElement: HTMLDivElement = target.querySelector(SELECTOR.HISTORY_TERM) as HTMLDivElement;
    const ul: HTMLUListElement = elem.closest(ELEMENT.UL) as HTMLUListElement;
    const position: number = Array.from(ul.children).indexOf(target);

    this.searchField.value = termElement.getAttribute(DATA.TERM) as string;
    const action = FALLBACK_SEARCH_URL(
      termElement.getAttribute(DATA.TERM) as string,
      {},
      termElement.getAttribute(DATA.TARGET) as string,
    );

    const urlObject = new SquirrelURL(action);
    const hashTrackingParameters = urlObject.trackingParameter;
    const trackingData: TrackingData = {
      [TRACK_LABEL.WK_SUGGEST_POS]: position + 1,
      [TRACK_LABEL.WK_SEARCH_HISTORY]: SanSearchHistory.EXISTS,
      [TRACK_LABEL.WK_SEARCH_HISTORY_EXISTS]: "true",
    };
    window.o_squirrel.suggestTracking.trackUsage(
      trackingData,
      hashTrackingParameters,
      false,
      false,
      false,
      true,
      window.o_squirrel.submitHistory.hasFilter(position),
    );
    window.o_squirrel.submitHistory.addSearch(this.searchField.value, position);
    window.o_squirrel.util.global.setLocation(urlObject.urlWithoutTrackingParameter);
  }
}

const instance = new SearchHistory();
export const searchHistory = {
  show: instance.show.bind(instance),
  clear: instance.clear.bind(instance),
  renderHistory: instance.renderHistory.bind(instance),
  clickEditAction: instance.clickEditAction.bind(instance),
  clickTermAction: instance.clickTermAction.bind(instance),
  clickDeleteAllAction: instance.clickDeleteAllAction.bind(instance),
};
